<script lang="ts">
	import {
		diagnosticsStore,
		errorCount,
		warningCount,
		type DiagnosticFilter
	} from '#lib/stores/diagnostics.js';
	import { t } from '#lib/stores/i18n.js';
	import { CheckCircle2 } from '@lucide/svelte';
	import DiagnosticIncidentItem, {
		type GroupedDiagnosticEntry
	} from './DiagnosticIncidentItem.svelte';
	import { SvelteMap } from 'svelte/reactivity';

	let selectedFilter: DiagnosticFilter = $state('all');
	let expandedId = $state<string | null>(null);

	let filteredEntries = $derived.by(() => {
		const entries = $diagnosticsStore;
		switch (selectedFilter) {
			case 'all':
				return entries;
			case 'errors':
				return entries.filter((e) => e.severity === 'error');
			case 'warnings':
				return entries.filter((e) => e.severity === 'warning');
			case 'api':
				return entries.filter((e) => e.category === 'api');
			case 'runtime':
				return entries.filter((e) => e.category === 'runtime');
		}
	});

	let groupedEntries = $derived.by<GroupedDiagnosticEntry[]>(() => {
		const entries = filteredEntries;
		if (entries.length === 0) return [];

		const groups: GroupedDiagnosticEntry[] = [];
		const groupMap = new SvelteMap<string, GroupedDiagnosticEntry>();

		for (const entry of entries) {
			const key = `${entry.severity}|${entry.category}|${(entry.title || '').trim()}|${(entry.message || '').trim()}|${entry.url || ''}`;
			const existing = groupMap.get(key);

			if (existing) {
				existing.count += 1;
				existing.firstTimestamp = entry.timestamp;
			} else {
				const group: GroupedDiagnosticEntry = {
					id: entry.id,
					severity: entry.severity,
					category: entry.category,
					title: entry.title,
					message: entry.message,
					details: entry.details,
					url: entry.url,
					stack: entry.stack,
					isThirdParty: entry.isThirdParty,
					count: 1,
					latestTimestamp: entry.timestamp,
					firstTimestamp: entry.timestamp
				};
				groupMap.set(key, group);
				groups.push(group);
			}
		}

		return groups;
	});

	function toggleExpand(id: string) {
		expandedId = expandedId === id ? null : id;
	}
</script>

<div class="space-y-4">
	<!-- Filters -->
	<div
		class="flex items-center gap-1 p-1 bg-surface-on/5 dark:bg-white/5 rounded-2xl border border-border/60 dark:border-white/10 flex-wrap"
	>
		<button
			type="button"
			class="px-3 py-1.5 text-xs rounded-xl font-medium transition-all cursor-pointer {selectedFilter ===
			'all'
				? 'bg-surface dark:bg-white/15 text-surface-on dark:text-white font-semibold shadow-xs'
				: 'text-surface-on/60 dark:text-slate-400 hover:text-surface-on dark:hover:text-white'}"
			onclick={() => (selectedFilter = 'all')}
		>
			{$t('diagnostics.filter.all')} ({$diagnosticsStore.length})
		</button>
		<button
			type="button"
			class="px-3 py-1.5 text-xs rounded-xl font-medium transition-all cursor-pointer {selectedFilter ===
			'errors'
				? 'bg-red-500/20 text-red-500 dark:text-red-300 font-semibold shadow-xs'
				: 'text-surface-on/60 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400'}"
			onclick={() => (selectedFilter = 'errors')}
		>
			{$t('diagnostics.filter.errors')} ({$errorCount})
		</button>
		<button
			type="button"
			class="px-3 py-1.5 text-xs rounded-xl font-medium transition-all cursor-pointer {selectedFilter ===
			'warnings'
				? 'bg-amber-500/20 text-amber-500 dark:text-amber-300 font-semibold shadow-xs'
				: 'text-surface-on/60 dark:text-slate-400 hover:text-amber-500 dark:hover:text-amber-400'}"
			onclick={() => (selectedFilter = 'warnings')}
		>
			{$t('diagnostics.filter.warnings')} ({$warningCount})
		</button>
		<button
			type="button"
			class="px-3 py-1.5 text-xs rounded-xl font-medium transition-all cursor-pointer {selectedFilter ===
			'api'
				? 'bg-purple-500/20 text-purple-600 dark:text-purple-300 font-semibold shadow-xs'
				: 'text-surface-on/60 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-300'}"
			onclick={() => (selectedFilter = 'api')}
		>
			{$t('diagnostics.filter.api')}
		</button>
		<button
			type="button"
			class="px-3 py-1.5 text-xs rounded-xl font-medium transition-all cursor-pointer {selectedFilter ===
			'runtime'
				? 'bg-orange-500/20 text-orange-600 dark:text-orange-300 font-semibold shadow-xs'
				: 'text-surface-on/60 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-300'}"
			onclick={() => (selectedFilter = 'runtime')}
		>
			{$t('diagnostics.filter.runtime')}
		</button>
	</div>

	<!-- Incident List / Empty State -->
	<div class="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
		{#if groupedEntries.length === 0}
			<div
				class="py-12 text-center flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 dark:border-white/10 bg-surface-on/2 dark:bg-white/2 p-6"
			>
				<div
					class="w-12 h-12 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 text-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/10 mb-3"
				>
					<CheckCircle2 class="w-6 h-6" />
				</div>
				<h3 class="text-sm font-bold text-surface-on dark:text-white mb-1">
					{$t('diagnostics.empty')}
				</h3>
				<p class="text-xs text-surface-on/60 dark:text-slate-400 max-w-md leading-relaxed">
					{$t('diagnostics.empty_subtitle')}
				</p>
				<div
					class="mt-4 inline-flex items-center gap-2 text-2xs text-surface-on/50 dark:text-slate-400 bg-surface-on/5 dark:bg-white/5 px-3.5 py-1.5 rounded-full border border-border/60 dark:border-white/10"
				>
					<span>{$t('diagnostics.open_shortcut')}</span>
					<kbd
						class="px-1.5 py-0.5 rounded bg-surface-on/10 dark:bg-white/10 font-mono font-semibold text-surface-on dark:text-slate-200"
						>Maj</kbd
					>
					<span>+</span>
					<kbd
						class="px-1.5 py-0.5 rounded bg-surface-on/10 dark:bg-white/10 font-mono font-semibold text-surface-on dark:text-slate-200"
						>D</kbd
					>
				</div>
			</div>
		{:else}
			{#each groupedEntries as entry (entry.id)}
				<DiagnosticIncidentItem
					{entry}
					isExpanded={expandedId === entry.id}
					ontoggle={() => toggleExpand(entry.id)}
				/>
			{/each}
		{/if}
	</div>
</div>
